import type { IGameState, IStage } from '.';
import { repeat } from '../util';
import { number, z, ZodSchema, type ZodErrorMap } from 'zod';

export const KwaliteitenJachtCardSchema = z.object({ name: z.string(), color: z.string().default('') });
export type KwaliteitenJachtCard = z.infer<typeof KwaliteitenJachtCardSchema>;
export const KwaliteitenJachtStarterCardSetSchema = z.tuple(repeat(7, KwaliteitenJachtCardSchema));
export const KwaliteitenJachtDataSchema = z
	.object({
		startingCards: z.tuple(repeat(12, KwaliteitenJachtStarterCardSetSchema)),
	})
	.default({ startingCards: repeat(12, repeat(7, { name: '', color: '' })) });

export type Stage = IStage | 'play-rounds' | 'play-ending-game';

export type KwaliteitenJachtPlayerState = {
	id: string;
	name: string;
	color: string;
	cards: KwaliteitenJachtCard[];
	relevantCards?: string[];
	// array of doubles you matched with another player
	// stored as <cardname>::<player-color>
	// we use this to ensure we don't allow users to register
	// the same double multiple times
	foundDoubles: string[];
	// used for validating card trading
	offeringCard?: string;
	// used for validating card buying
	buyingFrom?: string;
	coins: number;
	lastScreen: KwaliteitenJachtUpdate;
	// trade popup, sell popup, etc.
	prompt?: KwaliteitenJachtUpdate;
	score: number;
};

export type KwaliteitenJachtState = {
	stage: Stage;
	roundIndex: number;
	subRoundIndex: number;
	showPreface: boolean;
	players: KwaliteitenJachtPlayerState[];
	maxRounds: number;
};

export const KwaliteitenJachtUpdateSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('trigger-effect'), effect: z.enum(['confetti', 'red-flash']), text: z.string().optional() }),
	z.object({ kind: z.literal('show-login-screen'), failed: z.boolean().optional() }),
	z.object({ kind: z.literal('show-register-screen') }),
	z.object({
		kind: z.literal('show-story-screen'),
		title: z.string(),
		message: z.string(),
		image: z.enum(['day1', 'day2and3', 'day4', 'day5and6', 'day7']),
	}),
	z.object({
		kind: z.literal('show-swap-screen'),
		text: z.string(),
		round: z.number(),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-cards-screen'),
		title: z.string(),
		description: z.string(),
		cards: z.array(KwaliteitenJachtCardSchema),
		action: z.enum(['none', 'find-doubles', 'trade-card', 'buy-card']),
		playerOptions: z.array(z.object({ name: z.string(), color: z.string() })).optional(),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-qualitypicker-screen'),
		title: z.string(),
		message: z.string(),
		cards: z.array(KwaliteitenJachtCardSchema),
	}),
	z.object({
		kind: z.literal('show-results-screen'),
		players: z.array(z.object({ name: z.string(), color: z.string(), score: z.number() })),
	}),
	z.object({ kind: z.literal('show-message'), head: z.string(), body: z.string() }),
	z.object({
		kind: z.literal('update-players-state'),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
	}),
	z.object({
		kind: z.literal('update-player-state'),
		player: z.object({
			//
			name: z.string(),
			color: z.string(),
			cards: z.array(KwaliteitenJachtCardSchema),
			coins: z.number(),
		}),
	}),

	z.object({
		kind: z.literal('display-trade-offer'),
		card: z.string(),
		player: z.object({ name: z.string(), color: z.string() }),
	}),

	z.object({
		kind: z.literal('display-purchase-request'),
		player: z.object({ name: z.string(), color: z.string() }),
	}),

	z.object({
		kind: z.literal('finalized-purchase'),
	}),
]);
export type KwaliteitenJachtUpdate = z.infer<typeof KwaliteitenJachtUpdateSchema>;

export const KwaliteitenJachtMessageSchema = z.discriminatedUnion('kind', [
	// TODO: make it so this can only be sent by admin
	z.object({ kind: z.literal('start-game'), maxRounds: z.number() }),
	z.object({ kind: z.literal('end-preface') }),
	z.object({ kind: z.literal('end-early') }),
	z.object({ kind: z.literal('end-early-day') }),
	z.object({ kind: z.literal('force-restart-round') }),
	z.object({ kind: z.literal('set-player-active'), playerId: z.string(), active: z.boolean() }),

	z.object({ kind: z.literal('update-player-data'), playerId: z.string(), name: z.string(), color: z.string() }),
	z.object({ kind: z.literal('remove-player-data'), playerId: z.string() }),
	z.object({ kind: z.literal('double-found'), playerId: z.string(), otherPlayerColor: z.string(), cardName: z.string() }),

	z.object({ kind: z.literal('send-trade-offer'), playerId: z.string(), otherPlayerColor: z.string(), cardName: z.string() }),
	z.object({ kind: z.literal('finalize-trade'), playerId: z.string(), otherPlayerColor: z.string(), giveCard: z.string() }),

	z.object({ kind: z.literal('send-purchase-request'), playerId: z.string(), otherPlayerColor: z.string() }),
	z.object({ kind: z.literal('finalize-purchase'), playerId: z.string(), otherPlayerColor: z.string(), giveCard: z.string() }),
	z.object({ kind: z.literal('update-relevant-cards'), playerId: z.string(), relevantCards: z.array(z.string()) }),
]);

export type KwaliteitenJachtMessage = z.infer<typeof KwaliteitenJachtMessageSchema>;
